/**
 * `GET /api/settings` + `PUT /api/settings` (company_admin). Query key: ['settings'].
 * Stage 6: `GET /api/settings/connections` (['settings', 'connections']) and
 * `GET /api/settings/slack-channels` (['settings', 'slack-channels']) — both proxy
 * Innu Core with the caller's token; the channel list is `[]` until Slack is connected.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/lib/api-client';
import {
  normalizeChannels,
  normalizeConnections,
  normalizeSettings,
  type ConnectionsStatus,
  type Settings,
  type SettingsPatch,
  type SlackChannel,
} from './types';

export const SETTINGS_KEY = ['settings'] as const;
export const CONNECTIONS_KEY = [...SETTINGS_KEY, 'connections'] as const;
export const SLACK_CHANNELS_KEY = [...SETTINGS_KEY, 'slack-channels'] as const;

export function useConnections() {
  const client = useApiClient();
  return useQuery({
    queryKey: CONNECTIONS_KEY,
    queryFn: async () => normalizeConnections(await client.get<Partial<ConnectionsStatus>>('/api/settings/connections')),
    staleTime: 60 * 1000,
  });
}

/**
 * The company's Slack channels. Used by the recipient channel picker and, read-only,
 * by the Activity tab to turn a channel id into "#name". A core failure (502
 * `upstream_error`) is surfaced by the picker; everywhere else the id is shown instead.
 */
export function useSlackChannels(enabled = true) {
  const client = useApiClient();
  return useQuery({
    queryKey: SLACK_CHANNELS_KEY,
    queryFn: async () => normalizeChannels(await client.get<unknown>('/api/settings/slack-channels')),
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/** `id → "#name"` lookup; falls back to the id when the list is missing or lacks it. */
export function slackChannelLabel(channels: SlackChannel[] | undefined, id: string | null | undefined): string {
  if (!id) return '—';
  const hit = channels?.find((c) => c.id === id);
  return hit ? `#${hit.name}` : id;
}

export function useSettings() {
  const client = useApiClient();
  return useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: async () => normalizeSettings(await client.get<Partial<Settings>>('/api/settings')),
    staleTime: 30 * 1000,
  });
}

export function useSaveSettings() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: SettingsPatch) =>
      client.post<Partial<Settings> | null>('/api/settings', patch, { method: 'PUT' }),
    onSuccess: (data, patch) => {
      // The worker echoes the saved settings; fall back to the patch over the cached status block.
      const prev = qc.getQueryData<Settings>(SETTINGS_KEY);
      qc.setQueryData<Settings>(SETTINGS_KEY, normalizeSettings({ ...prev, ...(data ?? patch) }));
    },
  });
}
